// 📄 pages/Proyectos/AusenciasEquipo.tsx
// Lista de ausencias (vacaciones/permisos/licencias) de TODO el equipo de
// este proyecto — solo lectura acá, cada quien carga las suyas propias
// desde Mi Perfil > Mis ausencias (ver MisAusenciasSection.tsx). Vive en
// /proyecto/:projectId/ausencias, dentro de la sección "Proyecto" del
// sidebar.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, RefreshCw, Info } from 'lucide-react';
import { useProject } from '../../context/ProjectContext.tsx';
import { VacacionDeUsuario, getVacacionesDeUsuarios, tipoAusenciaLabel, seSuperponeConProximosDias } from '../../services/vacacionesService.ts';

export default function AusenciasEquipo() {
  const { project } = useProject();
  const [vacaciones, setVacaciones] = useState<VacacionDeUsuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const uids = project?.miembrosUids || [];
    if (uids.length === 0) { setLoading(false); return; }
    setLoading(true);
    getVacacionesDeUsuarios(uids)
      .then(setVacaciones)
      .catch((err) => {
        console.error('Error cargando las ausencias del equipo:', err);
        setError('No se pudieron cargar las ausencias del equipo.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.miembrosUids]);

  if (!project) return null;

  const nombrePorUid = new Map(project.miembros.map((m) => [m.userId, m.email]));
  const hoy = new Date().toISOString().slice(0, 10);
  const vigentesYFuturas = vacaciones.filter((v) => v.fechaFin >= hoy);

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Ausencias cargadas por cada miembro del equipo (dato propio de la persona, no de este proyecto). Para cargar las tuyas, andá a{' '}
          <Link to="/perfil" className="underline hover:text-blue-200">Mi Perfil</Link>.
        </span>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando ausencias del equipo...</p>
        </div>
      ) : vigentesYFuturas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Nadie del equipo tiene ausencias vigentes o futuras cargadas.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {vigentesYFuturas.map((v) => {
            const proxima = seSuperponeConProximosDias(v);
            return (
              <div key={`${v.userId}_${v.id}`} className={`flex items-center justify-between gap-3 bg-white/5 border rounded-2xl p-4 ${proxima ? 'border-amber-500/30' : 'border-white/10'}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{nombrePorUid.get(v.userId) || v.userId}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {v.fechaInicio} → {v.fechaFin} · {tipoAusenciaLabel(v.tipo)}
                    {v.notas ? ` · ${v.notas}` : ''}
                  </p>
                </div>
                {proxima && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30 flex-shrink-0">
                    Próxima semana
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
